import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GetRowIdParams, IServerSideDatasource, ValueGetterParams } from 'ag-grid-enterprise';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getCellRenderer, getValueGetter } from 'src/app/modules/core/components/ag-grid';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import { PicklistService } from 'src/app/modules/core/services/picklist/picklist.service';
import { DocumentReader, FieldDataType, SchemaField, SystemFieldType } from 'src/app/modules/core/store/models';
import * as fromRouting from 'src/app/modules/core/store/routing';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { DistributionList, DistributionListType } from 'src/app/modules/communications/store/communications/communications.model';
import { CommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import { v4 as uuidv4 } from 'uuid';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
  selector: 'app-distribution-list',
  templateUrl: './distribution-list.component.html',
  styleUrls: ['./distribution-list.component.scss'],
  standalone: false
})
export class DistributionListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DataTableServerComponent, { static: true }) dataTable: DataTableServerComponent;

  distributionLists: DistributionList[] = [];
  distributionListToDelete: DistributionList;
  destroy$ = new Subject<void>();
  datasource: any;
  columDefs: any[] = [];
  skip = 0;
  take = 10;
  totalCount: number = 0;
  isTableLoading: boolean;
  membersCount: any[];
  showType: boolean;

  selectedDistributionListType: DistributionListType = DistributionListType.None;
  distributionListTypes = [
    {
      value: DistributionListType.None,
      name: this.translateService.instant(`COMMUNICATIONS.DISTRIBUTION-LIST-TYPE-${DistributionListType.None}`)
    },
    {
      value: DistributionListType.FusionTeam,
      name: this.translateService.instant(`COMMUNICATIONS.DISTRIBUTION-LIST-TYPE-${DistributionListType.FusionTeam}`)
    }
  ];

  DistributionListType = DistributionListType;

  protected readonly userContext = inject(UserContextService);
  private notify = inject(SystemNotificationsService);

  constructor(
    private store: Store<fromComm.State>,
    private translateService: TranslateService,
    private gridHelper: GridHelperService,
    private picklistService: PicklistService,
    private commApiService: CommunicationsApiService
  ) {}

  ngOnInit(): void {
    if (!this.userContext.canReadDistributionList()) {
      this.notify.error(this.translateService.instant('COMMON.MISSING-PERMISSIONS'));
      return;
    }

    this.store
      .select(fromComm.selectSelectedDistributionListToDelete)
      .pipe(takeUntil(this.destroy$))
      .subscribe(distributionList => {
        this.distributionListToDelete = distributionList;
        if (!distributionList) {
          this.dataTable.refreshGrid();
        }
      });

    this.store
      .select(fromAuth.selectPermissionsChangeTime)
      .pipe(takeUntil(this.destroy$))
      .subscribe(time => {
        if (!!time) {
          this.columDefs = this.generateColumns();
          this.dataTable?.refreshGrid();
        }
      });

    this.columDefs = this.generateColumns();

    this.setFusionTypeEnabled();

    this.datasource = this.getServerSideDatasource();
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  setFusionTypeEnabled() {
    const query = {
      skip: 0,
      take: 1,
      where: 'DistributionListType = DistributionListType.FusionTeam'
    };

    this.commApiService.getDistributionLists(query).subscribe(resp => {
      this.showType = resp.totalCount > 0;
    });
  }

  getServerSideDatasource(): IServerSideDatasource {
    this.isTableLoading = true;
    return {
      getRows: params => {
        var translatedParams = this.gridHelper.gridParamsToRequestParams(params.request, this.getFilterClause());

        let sortField = 'Name';
        let sortDirection = 'Descending';
        if (params?.request.sortModel?.length > 0) {
          sortField = params?.request.sortModel[0].colId;
          sortDirection = params?.request.sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending';
        }

        const query = {
          skip: this.skip,
          take: this.take,
          sortField: sortField,
          sortDirection: sortDirection,
          where: translatedParams.where
        };

        this.commApiService.getDistributionLists(query).subscribe(
          resp => {
            const response = this.gridHelper.queryResultToGridData(resp);
            this.totalCount = response?.rowCount;

            const rowData = response?.rowData?.map(d => {
              const data = {
                ...d,
                membersCount: null
              };
              return data;
            });

            params.success({
              rowData: rowData,
              rowCount: response?.rowCount
            });

            this.getMembersCount(rowData);
          },
          err => params.fail()
        );
      }
    };
  }

  private getFilterClause(): string {
    return `DistributionListType = DistributionListType.${DistributionListType[this.selectedDistributionListType]}`;
  }

  private getMembersCount(dists: any[]): void {
    const uids = dists.map(n => n.uId);

    this.commApiService.getDistributionListMembersCount(uids).subscribe(resp => {
      this.membersCount = [];
      //const foo = [];
      resp.forEach(r => {
        const dist = dists.find(n => n.uId === r.distributionListUId);
        if (!!dist) {
          this.membersCount.push({
            uId: dist.uId,
            count: r.count
          });
          dist.membersCount = r.count;
          const rowNode = this.dataTable?.gridApi?.getRowNode(dist.uId);
          if (!!rowNode) {
            rowNode.setDataValue('membersCount', r.count);
          }
        }
      });
    });
  }

  distributionListTypeChanged(event: any): void {
    this.skip = 0;
    this.columDefs = this.generateColumns();
    this.dataTable?.refreshGrid();
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  private generateColumns() {
    var columns = [];

    const canEdit = this.userContext.canUpdateDistributionList();

    if (this.selectedDistributionListType == DistributionListType.None) {
      const canDelete = this.userContext.canDeleteDistributionList();

      if (canEdit || canDelete) {
        const actions = [];

        if (canEdit) {
          actions.push({
            label: this.translateService.instant(`MANAGEMENT.DATASETS.EDIT`),
            icon: 'icon-edit',
            click: data => {
              this.editClick(data);
            }
          });
        }

        if (canDelete) {
          actions.push({
            label: this.translateService.instant(`MANAGEMENT.DATASETS.DELETE`),
            icon: 'icon-delete_trash',
            click: data => {
              this.deleteClick(data);
            }
          });
        }

        columns.push({
          colId: '_actions',
          headerName: '',
          field: '',
          floatingFilter: false,
          sortable: false,
          resizable: false,
          minWidth: 64,
          suppressHeaderMenuButton: true,
          cellRenderer: 'actionCellComponent',
          cellRendererParams: {
            location: 'right',
            actions
          },
          width: 64,
          maxWidth: 64
        });
      }
    }

    const nameField = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.NAME'),
      systemName: 'name',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: nameField.displayName,
      field: nameField.systemName,
      colId: 'Name',
      sortable: true,
      resizable: true,
      flex: 2,
      menuTabs: [],
      cellRenderer: canEdit ? 'linkCellComponent' : 'textCellComponent',
      cellRendererParams: canEdit
        ? {
            schemaField: nameField,
            picklistService: this.picklistService,
            clicked: data => {
              this.editClick(data);
            }
          }
        : { schemaField: nameField },
      valueGetter: getValueGetter(FieldDataType.Text)
    });

    const updatedOn = {
      dataType: FieldDataType.DateTime,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.UPDATED-ON'),
      systemName: 'updatedOn',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: updatedOn.displayName,
      field: updatedOn.systemName,
      colId: 'UpdatedOn',
      sortable: true,
      resizable: true,
      flex: 1,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.DateTime),
      cellRendererParams: {
        schemaField: updatedOn,
        picklistService: this.picklistService
      },
      valueGetter: getValueGetter(FieldDataType.DateTime)
    });

    const editedBy = {
      dataType: FieldDataType.User,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.UPDATED-BY'),
      systemName: 'lastEditedById',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: editedBy.displayName,
      field: editedBy.systemName,
      colId: 'lastEditedById',
      sortable: true,
      resizable: true,
      flex: 1,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.User),
      cellRendererParams: {
        schemaField: editedBy,
        nameFieldName: 'lastEditedByName',
        picklistService: this.picklistService
      },
      valueGetter: getValueGetter(FieldDataType.User)
    });

    const members = {
      dataType: FieldDataType.Number,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.NUMBER-OF-MEMBERS'),
      systemName: 'membersCount',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: {
        format: 'NUMBER'
      }
    } as SchemaField;

    columns.push({
      headerName: members.displayName,
      field: members.systemName,
      colId: 'membersCount',
      sortable: false,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.Number),
      cellRendererParams: {
        schemaField: members,
        picklistService: this.picklistService
      },
      valueGetter: getValueGetter(FieldDataType.Number)
    });

    return columns;
  }

  ngOnDestroy(): void {
    this.gridHelper.clearSearchFilter();
    this.destroy$.next();
    this.destroy$.complete();
  }

  new() {
    this.store.dispatch(fromComm.newDistributionList());
  }

  navigate(path: string) {
    this.store.dispatch(
      fromRouting.go({
        routeData: { path: [path] }
      })
    );
  }

  editClick(distributionList: DistributionList) {
    this.store.dispatch(fromComm.editDistributionList({ distributionList }));
  }

  deleteClick(distributionList: DistributionList) {
    this.store.dispatch(fromComm.setDistributionListToDelete({ distributionList }));
  }

  confirmDeleteDistributionList(response: Boolean) {
    if (response) {
      this.store.dispatch(fromComm.deleteDistributionListApi({ distributionList: this.distributionListToDelete }));
    } else {
      this.store.dispatch(fromComm.clearDistributionListToDelete());
    }
  }

  getChannelRenderer = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    return this.translateService.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${value}`);
  };

  onGridDataLoaded(): void {
    this.isTableLoading = false;
  }

  getRowId = (params: GetRowIdParams): string => {
    return params.data.uId;
  };
}
